/**
 * Preview dev-only de /tesoreria/compensaciones (Sprint 17 Fase 2).
 *
 * Muestra la vista refactorizada a cards con stats + grid mensual.
 * 3 escenarios: con_compensaciones (mensual + semestral + única
 * pendiente + única aplicada + cerrada), solo_unicas, vacio.
 *
 * Los modales (Aplicar / Crear) se validan con login real — el
 * preview solo cubre la View.
 */
import { useState } from 'react';
import { CompensacionesView } from '../tesoreria/Compensaciones';
import type { CompensacionConDetalle } from '../../lib/api/types';

type Escenario = 'con_compensaciones' | 'solo_unicas' | 'vacio';

function mockComp(
  id: string,
  override: Partial<CompensacionConDetalle>,
): CompensacionConDetalle {
  return {
    id,
    lote_id: `l-${id}`,
    tenencia_id: `t-${id}`,
    descripcion: 'Mantención áreas verdes',
    categoria_id: 'cat-mant',
    monto: 80_000,
    periodicidad: 'mensual',
    proyecto_id: null,
    vigente_desde: '2026-01-01',
    vigente_hasta: '2026-12-31',
    aprobado_acta_url: null,
    notas: null,
    activa: true,
    motivo_cierre: null,
    cerrado_at: null,
    cerrado_por_usuario_id: null,
    aplicada_at: null,
    aplicada_por_usuario_id: null,
    creado_at: '2026-01-01T00:00:00Z',
    actualizado_at: '2026-01-01T00:00:00Z',
    lote_identificador: '6951',
    vecino_nombre: 'Guillermo Pérez',
    categoria_nombre: 'Mantención',
    consumido: 0,
    n_aplicaciones: 0,
    ultima_aplicacion_fecha: null,
    aplicaciones: [],
    cobertura: [],
    ...override,
  };
}

/** Cobertura de una cuota: meses `YYYY-MM` que pagó + abono sin mes completo. */
function cob(periodo: string, meses: string[], monto: number, abono = 0) {
  return { periodo, monto_aplicado: monto, meses, abono_parcial: abono };
}

function rango(anio: number, desde: number, hasta: number): string[] {
  return Array.from(
    { length: hasta - desde + 1 },
    (_, i) => `${anio}-${String(desde + i).padStart(2, '0')}`,
  );
}

function aplicacion(periodo: string, fecha: string, monto: number) {
  return {
    gasto_id: `g-${periodo}`,
    pago_id: `p-${periodo}`,
    periodo,
    monto,
    fecha,
    anulado: false,
  };
}

const ESCENARIOS: Record<Escenario, CompensacionConDetalle[]> = {
  con_compensaciones: [
    // Mensual con 3 aplicaciones (3/12)
    mockComp('c1', {
      lote_identificador: '6951',
      vecino_nombre: 'Guillermo Pérez',
      descripcion: 'Mantención áreas verdes',
      cobertura: [cob('2026-S1', rango(2026, 1, 3), 15_000)],
      categoria_nombre: 'Mantención',
      monto: 80_000,
      periodicidad: 'mensual',
      vigente_desde: '2026-01-01',
      vigente_hasta: '2026-12-31',
      consumido: 240_000,
      n_aplicaciones: 3,
      ultima_aplicacion_fecha: '2026-03-31',
      aplicaciones: [
        aplicacion('2026-03', '2026-03-31', 80_000),
        aplicacion('2026-02', '2026-02-28', 80_000),
        aplicacion('2026-01', '2026-01-31', 80_000),
      ],
    }),
    // Semestral (1/2)
    mockComp('c2', {
      lote_identificador: '6913',
      vecino_nombre: 'María Soto',
      descripcion: 'Reparación cancha multiuso',
      cobertura: [cob('2026-S1', rango(2026, 1, 6), 30_000)],
      categoria_nombre: 'Áreas comunes',
      monto: 450_000,
      periodicidad: 'semestral',
      vigente_desde: '2026-01-01',
      vigente_hasta: '2026-12-31',
      consumido: 450_000,
      n_aplicaciones: 1,
      ultima_aplicacion_fecha: '2026-02-15',
      aplicaciones: [aplicacion('2026-S1', '2026-02-15', 450_000)],
    }),
    // Única pendiente
    mockComp('c3', {
      lote_identificador: '6947',
      vecino_nombre: 'Patricia Muñoz',
      descripcion: 'Asesoría legal junta de vecinos',
      categoria_nombre: 'Administración',
      monto: 120_000,
      periodicidad: 'unica',
      vigente_desde: '2026-04-01',
      vigente_hasta: null,
      consumido: 0,
      n_aplicaciones: 0,
      ultima_aplicacion_fecha: null,
      aplicaciones: [],
    }),
    // Única ya aplicada
    mockComp('c4', {
      lote_identificador: '6912',
      vecino_nombre: 'Roberto Vega',
      descripcion: 'Diseño plano portón nuevo',
      cobertura: [cob('2026-S1', rango(2026, 2, 6), 25_000)],
      categoria_nombre: 'Mantención',
      monto: 250_000,
      periodicidad: 'unica',
      vigente_desde: '2026-03-01',
      vigente_hasta: null,
      consumido: 250_000,
      n_aplicaciones: 1,
      ultima_aplicacion_fecha: '2026-03-10',
      aplicaciones: [aplicacion('2026-03', '2026-03-10', 250_000)],
    }),
    // Bimestral en curso (4/6 esperado a la fecha)
    mockComp('c5', {
      lote_identificador: '6930',
      vecino_nombre: 'Carlos Núñez',
      descripcion: 'Mantención bomba de agua',
      categoria_nombre: 'Servicios',
      monto: 60_000,
      periodicidad: 'bimestral',
      vigente_desde: '2025-07-01',
      vigente_hasta: '2026-12-31',
      consumido: 240_000,
      n_aplicaciones: 4,
      ultima_aplicacion_fecha: '2026-03-15',
      aplicaciones: [
        aplicacion('2026-03', '2026-03-15', 60_000),
        aplicacion('2026-01', '2026-01-20', 60_000),
        aplicacion('2025-11', '2025-11-15', 60_000),
        aplicacion('2025-09', '2025-09-10', 60_000),
      ],
    }),
    // Única que salda la S1 y abona a la S2 (caso real 2026-10-06,
    // anonimizado): la grilla debe mostrar Ene–Jun y "$730 a cuenta de S2".
    mockComp('c-bolsas', {
      lote_identificador: '6900',
      vecino_nombre: 'Vecino Ejemplo',
      descripcion: 'Bolsas de basura plaza 2026',
      categoria_nombre: 'Bolsas Basura',
      monto: 30_730,
      periodicidad: 'unica',
      vigente_desde: '2026-01-01',
      vigente_hasta: null,
      consumido: 30_730,
      n_aplicaciones: 1,
      ultima_aplicacion_fecha: '2026-10-06',
      aplicada_at: '2026-10-06T12:30:00Z',
      aplicaciones: [aplicacion('2026-S1', '2026-10-06', 30_730)],
      cobertura: [
        cob('2026-S1', rango(2026, 1, 6), 30_000),
        cob('2026-S2', [], 730, 730),
      ],
    }),
    // Cerrada
    mockComp('c6', {
      lote_identificador: '6905',
      vecino_nombre: 'Ana Reyes',
      descripcion: 'Citofonía 2025 H2',
      cobertura: [cob('2025-S2', rango(2025, 7, 12), 30_000)],
      categoria_nombre: 'Servicios',
      monto: 25_000,
      periodicidad: 'mensual',
      vigente_desde: '2025-07-01',
      vigente_hasta: '2025-12-31',
      activa: false,
      motivo_cierre: 'Vencimiento natural del semestre acordado.',
      cerrado_at: '2026-01-02T10:00:00Z',
      cerrado_por_usuario_id: null,
      consumido: 150_000,
      n_aplicaciones: 6,
      ultima_aplicacion_fecha: '2025-12-31',
      aplicaciones: [
        aplicacion('2025-12', '2025-12-31', 25_000),
        aplicacion('2025-11', '2025-11-30', 25_000),
        aplicacion('2025-10', '2025-10-31', 25_000),
        aplicacion('2025-09', '2025-09-30', 25_000),
        aplicacion('2025-08', '2025-08-31', 25_000),
        aplicacion('2025-07', '2025-07-31', 25_000),
      ],
    }),
  ],
  solo_unicas: [
    mockComp('u1', {
      lote_identificador: '6925',
      vecino_nombre: 'Felipe Lara',
      descripcion: 'Reparación portón principal',
      categoria_nombre: 'Mantención',
      monto: 350_000,
      periodicidad: 'unica',
      vigente_desde: '2026-05-01',
      vigente_hasta: null,
      consumido: 0,
      n_aplicaciones: 0,
      ultima_aplicacion_fecha: null,
      aplicaciones: [],
    }),
    mockComp('u2', {
      lote_identificador: '6960',
      vecino_nombre: 'Sandra Pino',
      descripcion: 'Compra mangueras de riego',
      categoria_nombre: 'Mantención',
      monto: 95_000,
      periodicidad: 'unica',
      vigente_desde: '2026-04-15',
      vigente_hasta: null,
      consumido: 95_000,
      n_aplicaciones: 1,
      ultima_aplicacion_fecha: '2026-04-20',
      aplicaciones: [aplicacion('2026-04', '2026-04-20', 95_000)],
    }),
  ],
  vacio: [],
};

const SIDEBAR_ITEMS = [
  { label: 'Inicio' },
  { label: 'Casas' },
  { label: 'Pagos' },
  { label: 'Banco' },
  { label: 'Ingresos varios' },
  { label: 'Gastos' },
  { label: 'Compensaciones', active: true },
  { label: 'Recursos' },
  { label: 'Cuotas', proximamente: true },
  { label: 'Suspensiones', proximamente: true },
  { label: 'Cierres', proximamente: true },
  { label: 'Reportes', proximamente: true },
];

export function TesoreriaCompensacionesPreview() {
  const [escenario, setEscenario] = useState<Escenario>('con_compensaciones');
  const data = ESCENARIOS[escenario];

  return (
    <div className="min-h-screen bg-bg-page text-text-primary flex">
      <aside className="hidden md:flex w-64 bg-bg-sunken border-r-[0.5px] border-white/[0.08] flex-col">
        <div className="px-6 py-5 border-b-[0.5px] border-white/[0.08]">
          <div className="text-xs uppercase tracking-widest text-orange font-medium">
            Tesorería
          </div>
          <div className="text-base text-text-primary font-medium mt-1">
            Barrio Calbucura
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {SIDEBAR_ITEMS.map((it) => (
            <div
              key={it.label}
              className={`flex items-center justify-between px-3 py-2 rounded text-sm ${
                it.active
                  ? 'bg-bg-card text-text-primary font-medium'
                  : 'text-text-secondary'
              } ${it.proximamente ? 'opacity-70' : ''}`}
            >
              <span>{it.label}</span>
              {it.proximamente && (
                <span className="text-[10px] uppercase tracking-wide text-text-muted">
                  pronto
                </span>
              )}
            </div>
          ))}
        </nav>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-bg-card border-b-[0.5px] border-white/[0.08] px-4 md:px-8 py-3 flex items-center justify-between gap-4">
          <div className="text-sm text-text-secondary">
            Panel del tesorero · Compensaciones
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="text-text-muted hidden sm:inline">Escenario:</span>
            {(['con_compensaciones', 'solo_unicas', 'vacio'] as const).map(
              (e) => (
                <button
                  key={e}
                  type="button"
                  onClick={() => setEscenario(e)}
                  className={`px-2 py-1 rounded border ${
                    escenario === e
                      ? 'border-orange text-orange'
                      : 'border-white/[0.12] text-text-secondary hover:text-text-primary'
                  }`}
                >
                  {e}
                </button>
              ),
            )}
          </div>
        </header>

        <main className="flex-1 overflow-y-auto">
          <CompensacionesView
            compensaciones={data}
            loading={false}
            error={null}
            onAplicar={() => alert('Mock: abriría modal Aplicar (preview)')}
            onEditar={() => alert('Mock: abriría modal Editar (preview)')}
            onCerrar={() => alert('Mock: abriría modal Cerrar (preview)')}
            onEliminar={() => alert('Mock: abriría modal Eliminar (preview)')}
            onCrearNueva={() => alert('Mock: abriría modal Crear (preview)')}
            mensaje={null}
            onCerrarMensaje={() => {}}
          />
        </main>

        <footer className="bg-bg-page px-4 md:px-8 py-3 text-[11px] text-text-muted border-t border-white/[0.06]">
          Preview dev-only · Los modales se validan con login real.
        </footer>
      </div>
    </div>
  );
}
